import { ComponentProps } from "react";
import { useSetPageParam } from "@hooks/useSetPageParam";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import { ImagePresets } from "@routes/Article/components/ArticleThumbnail/utils/enums";
import SeasonDivider from "@routes/Section/layouts/BTLuxe/components/SeasonDivider";
import { getSeasonBasedOnDate } from "@routes/Section/layouts/Lifestyle/utils/helpers";
import { cn } from "@util/helpers";
import dayjs from "dayjs";

type BTLuxeListingCardProps = {
  /** The initial length of the card props. */
  initialLength: number;
  /** The all card props. */
  allCardProps: ComponentProps<typeof Card>[];
  /** The card prop. */
  cardProp: ComponentProps<typeof Card>;
  /** The todays season. */
  todaysSeason?: string;
  /** The index of the card. */
  index: number;
};

const BTLuxeListingCard = ({
  index,
  todaysSeason,
  initialLength,
  allCardProps,
  cardProp,
}: BTLuxeListingCardProps): React.ReactElement => {
  const { ref } = useSetPageParam({ cardIndex: index });

  const previousIndex = index - 1 > 0 ? index - 1 : 0;
  const publishedDate = cardProp.updatedTime;
  const prevPublishedDate = allCardProps[previousIndex].updatedTime;

  if (typeof publishedDate === "undefined") return <></>;
  if (typeof prevPublishedDate === "undefined") return <></>;

  const previousSeason =
    index <= initialLength
      ? todaysSeason
      : getSeasonBasedOnDate(prevPublishedDate);
  const year = dayjs(publishedDate).year();
  const currentSeason = getSeasonBasedOnDate(publishedDate);
  const isDisplayDivider =
    index > initialLength - 1 && previousSeason !== currentSeason;

  const variant: CardVariations = {
    image: {
      position: "",
      width: "w-full",
      aspectRatio:
        "aspect-3x2 md:aspect-[348/385] lg:aspect-3x2 group-hover/card:lg:scale-[1.02] transition-all ease-in duration-[350ms] h-auto object-cover w-full",
      extraClass: "overflow-hidden",
      filters: { w: ImagePresets.MEDIUM, dpr: 1, f: "webp" },
    },
    content: {
      width: "w-full",
      extraClass: "pt-3",
      blurb: {
        extraClass:
          "font-oceanSansStd mt-3 !block break-words text-lg font-light leading-[1.3] text-gray-750",
      },
      updatedTimeWrapperClass: "hidden",
    },
    title: {
      color: "!text-gray-850",
      size: "text-4xl md:text-8xl",
      extraClass:
        "font-ivyMode my-0 break-words font-semibold uppercase leading-tight transition-all duration-[350ms] hover:underline hover:underline-offset-4",
    },
    kicker: {
      color: "text-verticals-lifestyle-luxe",
      extraClass: "text-xs tracking-17% font-oceanSansStd font-extrabold mb-1",
    },
  };

  return (
    <>
      {isDisplayDivider ? (
        <SeasonDivider
          season={currentSeason}
          year={year}
          className="col-span-1 md:col-span-6"
        />
      ) : null}

      <div
        className={cn(
          "col-span-1 h-full w-full pb-4 pt-5 first:border-t-0 md:col-span-2 md:border-0 md:py-0",
          {
            "md:col-span-3": [0, 1].includes(index),
            "border-t border-gray-175": !isDisplayDivider,
          }
        )}
        ref={ref}
      >
        <Card {...cardProp} variations={variant} className="group/card" />
      </div>
    </>
  );
};

export default BTLuxeListingCard;
